import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from '../../../../../redux/store';
import { authSelectors } from '../../../../../redux/app/auth';
import moment from 'moment';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { vesselSelectors } from 'redux/app/vessel';

type OwnProps = {
	vesselId: string;
};
type GRB2CoverPageProps = PropsFromRedux & OwnProps;
function GRB2CoverPage(props: GRB2CoverPageProps) {
    const { dateRange, vesselName, imoNumber, distinctiveNumberOrLetters } =
        props;
    return (
        <>
            {/* First Page */}
            <div className="flex flex-col py-4 px-8 mb-2 h-screen">
                <div className="font-medium text-center text-xl my-20 mx-0">
                    <h3 className="uppercase">RECORD OF GARBAGE DISCHARGES</h3>
                    <h3>PART II</h3>
                    <h4>
                        For all cargo residues as defined in regulation 1.2
                        (Definitions)
                    </h4>
                    <h4>(Ships that carry solid bulk cargoes)</h4>
                </div>
                <div className="flex justify-center mt-8">
                    <div className="w-2/3 mt-2.5">
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Name of ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {vesselName}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Distinctive number or letters:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {distinctiveNumberOrLetters}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">IMO Number:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {imoNumber}
                            </p>
                        </div>

                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Gross tonnage:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {props?.tonnage?.grossTonnage || ''}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <div
                                style={{ width: '54%' }}
                                className="flex items-center  mb-2"
                            >
                                <p className="mr-2 min-w-fit">Period from :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.startDate &&
                                        moment(dateRange?.startDate).format(
                                            'DD-MM-yyyy'
                                        )}{' '}
                                </p>
                            </div>
                            <div
                                style={{ width: '45%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">to :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.endDate &&
                                        moment(dateRange?.endDate).format(
                                            'DD-MM-yyyy'
                                        )}
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Working language of the ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                English
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {/* Second Page */}
            <div className="newpage flex flex-col justify-between py-4 pt-0 px-8 mb-2 text-left grb2">
                <ol className="list-decimal ml-4">
                    <li className="my-4 mx-0">
                        <b>Introduction</b>
                        <p>
                            In accordance with regulation 10 of Annex V of the
                            International Convention for the Prevention of
                            Pollution from Ships, 1973, as modified by the
                            Protocol of 1978 (MARPOL), a record is to be kept of
                            each discharge operation or completed incineration.
                            This includes discharges into the sea, to reception
                            facilities or to other ships, as well as accidental
                            loss of garbage.
                        </p>
                    </li>
                    <li className="my-4 mx-0">
                        <b>Garbage and garbage management</b>
                        <p>
                            Garbage means all kinds of food wastes, domestic
                            wastes and operational wastes, all plastics, cargo
                            residues, incinerator ashes, cooking oil, fishing
                            gear, and animal carcasses generated during the
                            normal operation of the ship and liable to be
                            disposed of continuously or periodically except
                            those substances which are defined or listed in
                            other Annexes to the present Convention. Garbage
                            does not include fresh fish and parts thereof
                            generated as a result of fishing activities
                            undertaken during the voyage, or as a result of
                            aquaculture activities which involve the transport
                            of fish including shellfish for placement in the
                            aquaculture facility and the transport of harvested
                            fish including shellfish from such facilities to
                            shore for processing.
                        </p>
                        <p>
                            The Guidelines for the Implementation of MARPOL
                            Annex V <sup className="text-xs">1</sup> should also
                            be referred to for relevant information.
                        </p>
                    </li>
                    <li className="my-4 mx-0">
                        <b>Description of the garbage</b>
                        <p>
                            Garbage is to be grouped into categories for the
                            purposes of recording in parts I and II of the
                            Garbage Record Book as follows:
                        </p>
                        <div className="ml-4">
                            <b>Part I</b>
                            <ol
                                className="list-alpha ml-4"
                                style={{ marginLeft: '-1.2rem' }}
                                type="A"
                            >
                                <li className="my-1 mx-0">
                                    <span className="ml-2">Plastics</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">Food wastes</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Domestic wastes
                                    </span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">Cooking oil</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Incinerator ashes
                                    </span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Operational wastes
                                    </span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Animal carcasses
                                    </span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">Fishing gear</span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">E-waste</span>
                                </li>
                            </ol>
                        </div>
                        <div className="my-6 mx-4">
                            <b>Part II</b>
                            <ol
                                className="list-alpha ml-4"
                                style={{ marginLeft: '-1.2rem' }}
                                type="A"
                                start={10}
                            >
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Cargo residues (non-HME)
                                    </span>
                                </li>
                                <li className="my-1 mx-0">
                                    <span className="ml-2">
                                        Cargo residues (HME)
                                    </span>
                                </li>
                            </ol>
                        </div>
                    </li>
                    {/* Third Page */}
                    <li className="newpage my-4 mx-0 text-left">
                        <b className="italic">
                            Entries in the Garbage Record Book
                        </b>
                        <div className="flex items-start mb-2.5">
                            <div className="min-w-10 my-1 mx-0">4.1</div>
                            <div>
                                <p className="my-1 mx-0">
                                    Entries in the Garbage Record Book shall be
                                    made on each of the following occasions:
                                </p>
                                {/* First sub point */}
                                <div className="flex items-start">
                                    <div className="min-w-10 my-1 mx-0">
                                        4.1.1
                                    </div>
                                    <div>
                                        <p className="my-1 mx-0">
                                            When garbage is discharged to a
                                            reception facility{' '}
                                            <sup className="text-xs">2</sup>{' '}
                                            ashore or to other ships:
                                        </p>
                                        <ol className="list-decimal ml-4">
                                            <li className="my-2 mx-0">
                                                Date and time of discharge
                                            </li>
                                            <li className="my-2 mx-0">
                                                Port or facility, or name of
                                                ship
                                            </li>
                                            <li className="my-2 mx-0">
                                                Categories of garbage discharged
                                            </li>
                                            <li className="my-2 mx-0">
                                                Estimated amount discharged for
                                                each category in cubic metres
                                            </li>
                                            <li className="my-2 mx-0">
                                                Signature of the officer in
                                                charge of the operation.
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                                {/* Second sub point */}
                                <div className="flex items-start">
                                    <div className="min-w-10 my-1 mx-0">
                                        4.1.2
                                    </div>
                                    <div>
                                        <p className="my-1 mx-0">
                                            When garbage is incinerated:
                                        </p>
                                        <ol className="list-decimal ml-4">
                                            <li className="my-2 mx-0">
                                                Date and time of start and stop
                                                of incineration
                                            </li>
                                            <li className="my-2 mx-0">
                                                Position of the ship (latitude
                                                and longitude) at the start and
                                                stop of incineration
                                            </li>
                                            <li className="my-2 mx-0">
                                                Categories of garbage
                                                incinerated
                                            </li>
                                            <li className="my-2 mx-0">
                                                Estimated amount incinerated in
                                                cubic metres
                                            </li>
                                            <li className="my-2 mx-0">
                                                Signature of the officer in
                                                charge of the operation.
                                            </li>
                                        </ol>
                                    </div>
                                </div>

                                {/* Third sub point */}
                                <div className="flex items-start">
                                    <div className="min-w-10 my-1 mx-0">
                                        4.1.3
                                    </div>
                                    <div>
                                        <p className="my-1 mx-0">
                                            When garbage is discharged into the
                                            sea in accordance with regulation 4,
                                            5 or 6 of MARPOL Annex V or chapter
                                            5 of part II-A of the Polar Code:
                                        </p>
                                        <ol className="list-decimal ml-4">
                                            <li className="my-2 mx-0">
                                                Date and time of discharge
                                            </li>
                                            <li className="my-2 mx-0">
                                                Position of the ship (latitude
                                                and longitude). Note: for cargo
                                                residue discharges, include
                                                discharge start and stop
                                                positions
                                            </li>
                                            <li className="my-2 mx-0">
                                                Category of garbage discharged
                                            </li>
                                            <li className="my-2 mx-0">
                                                Estimated amount discharged for
                                                each category in cubic metres
                                            </li>
                                            <li className="my-2 mx-0">
                                                Signature of the officer in
                                                charge of the operation.
                                            </li>
                                        </ol>
                                    </div>
                                </div>

                                {/* Fourth sub point */}
                                <div className="flex items-start">
                                    <div className="min-w-10 my-1 mx-0">
                                        4.1.4
                                    </div>
                                    <div>
                                        <p className="my-1 mx-0">
                                            Accidental or other exceptional
                                            discharges or loss of garbage into
                                            the sea, including in accordance
                                            with regulation 7 of Annex V of
                                            MARPOL:
                                        </p>
                                        <ol className="list-decimal ml-4">
                                            <li className="my-2 mx-0">
                                                Date and Time of occurrence
                                            </li>
                                            <li className="my-2 mx-0">
                                                Port or position of the ship at
                                                the time of occurrence
                                                (latitude, longitude and water
                                                depth if known)
                                            </li>
                                            <li className="my-2 mx-0">
                                                Categories of garbage discharged
                                                or lost
                                            </li>
                                            <li className="my-2 mx-0">
                                                Estimated amount for each
                                                category in cubic metres
                                            </li>
                                            <li className="my-2 mx-0">
                                                The reason for the discharge or
                                                loss and general remarks.
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start mb-2.5">
                            <div className="min-w-10 my-1 mx-0">4.2</div>
                            <div>
                                <p className="my-1 mx-0">
                                    The amount of garbage on board should be
                                    estimated in cubic metres, if possible
                                    separately according to category. The
                                    Garbage Record Book contains many references
                                    to estimated amount of garbage. It is
                                    recognized that the accuracy of estimating
                                    amounts of garbage is left to
                                    interpretation. Volume estimates will differ
                                    before and after processing. Some processing
                                    procedures may not allow for a usable
                                    estimate of volume, e.g., the continuous
                                    processing of food waste. Such factors
                                    should be taken into consideration when
                                    making and interpreting entries made in a
                                    record.
                                </p>
                            </div>
                        </div>
                    </li>
                </ol>

                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-8 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div>
                    <div className="my-2 mx-0 flex items-start">
                        <sup className="text-xs mr-1.5 mt-2">1</sup>
                        <span className="text-sm">
                            Refer to the Guidelines for the Implementation of
                            Annex V of MARPOL 73/78, as amended by resolutions.
                        </span>
                    </div>
                    <div className="my-2 mx-0 flex items-start">
                        <sup className="text-xs mr-1.5 mt-2">2</sup>
                        <span className="text-sm">
                            Ship's masters should obtain from the operator of
                            the reception facilities, which includes barges and
                            trucks, a receipt or certificate specifying the
                            estimated amount of garbage transferred. The
                            receipts or certificates must be kept together with
                            the Garbage Record Book.
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
    const inspectorStore = inspectorSelectors._getStore(store);
    const dateRange = inspectorSelectors.getDateRange(inspectorStore);
	const vesselStore = vesselSelectors._getStore(store);
	const vesselInfo = vesselSelectors.getVesselById(
		ownProps.vesselId,
		vesselSelectors.getVessels(vesselStore)
	);
    return {
        dateRange,
        vesselName: vesselInfo.vesselName,
        imoNumber: vesselInfo.imoNumber,
        tonnage: vesselInfo.tonnage,
        distinctiveNumberOrLetters: vesselInfo?.distinctiveNumberOrLetters,
    };
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
    return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(GRB2CoverPage);
